import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef } from "react";
import { useScrollTracking } from "@/hooks/use-scroll-tracking";
import { ConditionalLayout } from "@/components/ConditionalLayout";
import { CanonicalUrl } from "@/components/CanonicalUrl";
import { MetaDescription } from "@/components/MetaDescription";
import { PageTitle } from "@/components/PageTitle";
import { HreflangTags } from "@/components/HreflangTags";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { NewsletterToast } from "@/components/NewsletterToast";

// Only the landing page is loaded eagerly — every other route is lazy so its
// code stays out of the main bundle (Lighthouse: "reduce unused JavaScript")
import Index from "./pages/Index";

// Less critical routes loaded lazily
const Functionalities = lazy(() => import("./pages/Functionalities"));
const Pricing = lazy(() => import("./pages/Pricing"));
const SuccessStories = lazy(() => import("./pages/SuccessStories"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const SuccessStoryDetail = lazy(() => import("./pages/SuccessStoryDetail"));
const UseCase = lazy(() => import("./pages/UseCase"));
const Team = lazy(() => import("./pages/Team"));
const Partners = lazy(() => import("./pages/Partners"));
const Downloads = lazy(() => import("./pages/Downloads"));
const DownloadThankYou = lazy(() => import("./pages/DownloadThankYou"));
const Events = lazy(() => import("./pages/Events"));
const Careers = lazy(() => import("./pages/Careers"));
const CareerDetail = lazy(() => import("./pages/CareerDetail"));
const Definitions = lazy(() => import("./pages/Definitions"));
const DefinitionDetail = lazy(() => import("./pages/DefinitionDetail"));
const BattleCards = lazy(() => import("./pages/BattleCards"));
const CostOfInaction = lazy(() => import("./pages/CostOfInaction"));
const SmartDataForge = lazy(() => import("./pages/SmartDataForge"));
const EndToEndProcess = lazy(() => import("./pages/EndToEndProcess"));
const ExpertTalk = lazy(() => import("./pages/ExpertTalk"));
const BattleCardDetail = lazy(() => import("./pages/BattleCardDetail"));
const TopTenList = lazy(() => import("./pages/TopTenList"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Imprint = lazy(() => import("./pages/Imprint"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const AIAgents = lazy(() => import("./pages/AIAgents"));
const Minerva = lazy(() => import("./pages/Minerva"));
const AgentLibrary = lazy(() => import("./pages/AgentLibrary"));
const Processes = lazy(() => import("./pages/Processes"));
const AgentDetail = lazy(() => import("./pages/AgentDetail"));
const CompetenceCenter = lazy(() => import("./pages/CompetenceCenter"));

const queryClient = new QueryClient();

// The tracking code replaces the plain queue's push with its own once it has
// loaded. Until then, pushes would land in the queue that is replayed before
// its automatic initial page view — and change that view's path.
const isHubSpotReady = () =>
  typeof window !== 'undefined' &&
  !!window._hsq &&
  Object.prototype.hasOwnProperty.call(window._hsq, 'push');

// Component to track HubSpot page views on route changes for SPA
function HubSpotPageViewTracker() {
  const location = useLocation();
  const isEntryPage = useRef(true);

  useEffect(() => {
    // The entry page is recorded by the tracking code's own initial page view
    // (index.html queues its path), so only client-side navigations count here.
    if (isEntryPage.current) {
      isEntryPage.current = false;
      return;
    }

    // The tracking code loads late (first interaction or idle, see index.html),
    // and the click that triggers it is often a navigation. Wait for it.
    const path = location.pathname + location.search;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const track = () => {
      if (isHubSpotReady()) {
        window._hsq!.push(['setPath', path]);
        window._hsq!.push(['trackPageView']);
      } else if (++attempts < 60) {
        timer = setTimeout(track, 500);
      }
    };
    track();
    return () => clearTimeout(timer);
  }, [location]);

  return null;
}

// Component to track scroll depth events for GA4
function ScrollDepthTracker() {
  useScrollTracking();
  return null;
}

/**
 * Providers that do not depend on a router. Exported so the build-time
 * prerender entry (src/entry-server.tsx) can mount the same tree.
 */
export const AppProviders = ({ children }: { children: React.ReactNode }) => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>{children}</TooltipProvider>
  </QueryClientProvider>
);

/**
 * Everything below the router. The browser entry wraps this in a
 * <BrowserRouter>, the prerender entry in a <StaticRouter>, so both render
 * exactly the same page for a given URL.
 */
export const AppRoutes = () => (
  <>
        <HubSpotPageViewTracker />
        <ScrollDepthTracker />
        <CanonicalUrl />
        <LanguageProvider>
          <NewsletterToast />
          <MetaDescription />
          <PageTitle />
          <HreflangTags />
          <ConditionalLayout>
            <Suspense fallback={<div className="min-h-screen" data-suspense-fallback="" />}>
            <Routes>
              {/* Redirect root to German home */}
              <Route path="/" element={<Navigate to="/de" replace />} />
              
              {/* Language-prefixed routes */}
              <Route path="/de" element={<Index />} />
              <Route path="/en" element={<Index />} />
              
              {/* Functionalities */}
              <Route path="/de/plattform" element={<Functionalities />} />
              <Route path="/en/platform" element={<Functionalities />} />
              
              {/* Pricing */}
              <Route path="/de/preise" element={<Pricing />} />
              <Route path="/en/pricing" element={<Pricing />} />
              
              {/* Success Stories */}
              <Route path="/de/success-stories" element={<SuccessStories />} />
              <Route path="/en/success-stories" element={<SuccessStories />} />
              
              {/* Success Story Detail */}
              <Route path="/de/success-story/:companyName" element={<SuccessStoryDetail />} />
              <Route path="/en/success-story/:companyName" element={<SuccessStoryDetail />} />
              
              {/* Partners */}
              <Route path="/de/partner" element={<Partners />} />
              <Route path="/en/partners" element={<Partners />} />
              
              {/* Team */}
              <Route path="/de/team" element={<Team />} />
              <Route path="/en/team" element={<Team />} />
              
              {/* Events */}
              <Route path="/de/veranstaltungen" element={<Events />} />
              <Route path="/en/events" element={<Events />} />
              
              {/* Careers */}
              <Route path="/de/karriere" element={<Careers />} />
              <Route path="/en/careers" element={<Careers />} />
              <Route path="/de/karriere/:jobId" element={<CareerDetail />} />
              <Route path="/en/careers/:jobId" element={<CareerDetail />} />

              {/* Definitions / Knowledge Base */}
              <Route path="/de/definitionen" element={<Definitions />} />
              <Route path="/en/definitions" element={<Definitions />} />
              <Route path="/de/definitionen/:slug" element={<DefinitionDetail />} />
              <Route path="/en/definitions/:slug" element={<DefinitionDetail />} />

              {/* Battle Cards / Competitive comparison */}
              <Route path="/de/battle-cards" element={<BattleCards />} />
              <Route path="/en/battle-cards" element={<BattleCards />} />
              <Route path="/de/battle-cards/:slug" element={<BattleCardDetail />} />
              <Route path="/en/battle-cards/:slug" element={<BattleCardDetail />} />
              <Route path="/de/battle-cards/top-10/:slug" element={<TopTenList />} />
              <Route path="/en/battle-cards/top-10/:slug" element={<TopTenList />} />

              {/* FAQ hub */}
              <Route path="/de/faq" element={<FAQ />} />
              <Route path="/en/faq" element={<FAQ />} />

              {/* Cost-of-Inaction calculator landing page */}
              <Route path="/de/cost-of-inaction" element={<CostOfInaction />} />
              <Route path="/en/cost-of-inaction" element={<CostOfInaction />} />

              {/* Smart Data Forge lead magnet */}
              <Route path="/de/smart-data-forge" element={<SmartDataForge />} />
              <Route path="/en/smart-data-forge" element={<SmartDataForge />} />

              {/* "Vier Experten, fünf Meinungen" talk series with Miragon */}
              <Route path="/de/vier-experten-fuenf-meinungen" element={<ExpertTalk />} />
              <Route path="/en/four-experts-five-opinions" element={<ExpertTalk />} />

              {/* End-to-end processes: overview and one page per process (Lead-to-Activation, …) */}
              <Route path="/de/prozesse" element={<Processes />} />
              <Route path="/en/processes" element={<Processes />} />
              <Route path="/de/prozesse/:processSlug" element={<EndToEndProcess />} />
              <Route path="/en/processes/:processSlug" element={<EndToEndProcess />} />

              {/* Downloads */}
              <Route path="/de/downloads" element={<Downloads />} />
              <Route path="/en/downloads" element={<Downloads />} />
              
              {/* Download Thank You */}
              <Route path="/de/download-vielen-dank" element={<DownloadThankYou />} />
              <Route path="/en/download-thank-you" element={<DownloadThankYou />} />
              
              {/* Use Cases */}
              <Route path="/de/use-cases/:useCaseName" element={<UseCase />} />
              <Route path="/en/use-cases/:useCaseName" element={<UseCase />} />
              
              {/* Contact */}
              <Route path="/de/kontakt" element={<ContactUs />} />
              <Route path="/en/contact" element={<ContactUs />} />
              
              {/* Imprint */}
              <Route path="/de/impressum" element={<Imprint />} />
              <Route path="/en/imprint" element={<Imprint />} />
              
              {/* Privacy */}
              <Route path="/de/datenschutz" element={<PrivacyPolicy />} />
              <Route path="/en/privacy" element={<PrivacyPolicy />} />
              
              {/* Terms */}
              <Route path="/de/nutzungsbedingungen" element={<TermsOfService />} />
              <Route path="/en/terms" element={<TermsOfService />} />
              
              {/* AI Agents */}
              <Route path="/de/frontier-agents" element={<AIAgents />} />
              <Route path="/en/frontier-agents" element={<AIAgents />} />

              {/* Minerva 2.0 product landing page */}
              <Route path="/de/minerva" element={<Minerva />} />
              <Route path="/en/minerva" element={<Minerva />} />

              {/* Minerva Agent Library: overview and one page per agent */}
              <Route path="/de/minerva/agenten" element={<AgentLibrary />} />
              <Route path="/en/minerva/agents" element={<AgentLibrary />} />
              <Route path="/de/minerva/agenten/:agentSlug" element={<AgentDetail />} />
              <Route path="/en/minerva/agents/:agentSlug" element={<AgentDetail />} />

              {/* Kompetenzcenter Süddeutschland: hub, one page per state and per city */}
              <Route path="/de/kompetenzcenter" element={<CompetenceCenter />} />
              <Route path="/en/competence-center" element={<CompetenceCenter />} />
              <Route path="/de/kompetenzcenter/:regionSlug" element={<CompetenceCenter />} />
              <Route path="/en/competence-center/:regionSlug" element={<CompetenceCenter />} />
              <Route path="/de/kompetenzcenter/:regionSlug/:citySlug" element={<CompetenceCenter />} />
              <Route path="/en/competence-center/:regionSlug/:citySlug" element={<CompetenceCenter />} />

              {/* Maintenance */}
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/de/wartung" element={<Maintenance />} />
              <Route path="/en/maintenance" element={<Maintenance />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </ConditionalLayout>
        </LanguageProvider>
  </>
);

const App = () => (
  <AppProviders>
    <Toaster />
    <Sonner />
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppRoutes />
    </BrowserRouter>
  </AppProviders>
);

export default App;
